import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, Clock, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { submitContactForm, requestCallback, erpPrograms } from '@/lib/api';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    company: '', job_title: '', erp_interest: '', training_track: '', message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('Please fill in required fields');
      return;
    }
    setLoading(true);
    try {
      await submitContactForm({
        ...formData,
        erp_interest: formData.erp_interest ? [formData.erp_interest] : []
      });
      toast.success('Thank you! We will contact you soon.');
      setFormData({ first_name: '', last_name: '', email: '', phone: '', company: '', job_title: '', erp_interest: '', training_track: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      <section className="py-16 lg:py-24 bg-gradient-to-br from-brand-50 via-white to-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12">
            <div  >
              <span className="inline-block px-4 py-2 bg-brand-100 text-brand-700 rounded-full text-sm font-medium mb-6">Contact Us</span>
              <h1 className="text-4xl sm:text-5xl font-bold text-brand-900 mb-6">Let's Discuss Your ERP Training Needs</h1>
              <p className="text-lg text-slate-600 mb-8">Have questions? Want to learn more? Fill out the form and our team will get back to you within 24 hours.</p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center"><Mail className="w-5 h-5 text-brand-700" /></div>
                  <div><p className="text-sm text-slate-500">Email</p><p className="font-medium text-brand-900">info@learnquanta.com</p></div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center"><Phone className="w-5 h-5 text-brand-700" /></div>
                  <div><p className="text-sm text-slate-500">Phone</p><p className="font-medium text-brand-900">+46 760 085 362</p></div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center"><Clock className="w-5 h-5 text-brand-700" /></div>
                  <div><p className="text-sm text-slate-500">Response Time</p><p className="font-medium text-brand-900">Within 24 hours</p></div>
                </div>
              </div>
            </div>
            
            <div   >
              <Card className="shadow-lg">
                <CardHeader><CardTitle>Send us a Message</CardTitle></CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">First Name *</Label>
                        <Input id="first_name" value={formData.first_name} onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))} data-testid="contact-first-name" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Last Name *</Label>
                        <Input id="last_name" value={formData.last_name} onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))} data-testid="contact-last-name" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} data-testid="contact-email" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone</Label>
                      <Input id="phone" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))} data-testid="contact-phone" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input id="company" value={formData.company} onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="job_title">Job Title</Label>
                        <Input id="job_title" value={formData.job_title} onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>ERP Interest</Label>
                      <Select value={formData.erp_interest} onValueChange={(value) => setFormData(prev => ({ ...prev, erp_interest: value }))}>
                        <SelectTrigger data-testid="contact-erp-select"><SelectValue placeholder="Select ERP" /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(erpPrograms).map(([key, program]) => (
                            <SelectItem key={key} value={key}>{program.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea id="message" rows={4} value={formData.message} onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))} placeholder="Tell us about your training needs..." data-testid="contact-message" />
                    </div>
                    <Button type="submit" className="btn-primary w-full" disabled={loading} data-testid="contact-submit-btn">
                      <Send className="w-4 h-4 mr-2" />{loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-700">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Prefer to Talk?</h2>
            <p className="text-brand-100 mb-8">Schedule a free consultation call with our career advisors.</p>
            <Link to="/webinar"><Button className="bg-white text-brand-700 hover:bg-brand-50 rounded-full px-8 py-3 font-medium">Schedule a Call</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
